
import React, { useState, useEffect } from 'react';
import { ViewRouter } from "@/components/views/ViewRouter";
import { AuthGuard } from '@/components/auth/AuthGuard';
import { SessionManager } from '@/components/auth/SessionManager';
import { useIndexState } from '@/hooks/useIndexState';
import { useAuth } from '@/components/auth/AuthProvider';
import { useAdminAuth } from '@/hooks/useAdminAuth';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUserMode } from '@/stores/userModeStore';
import { AdminPinAuth } from '@/components/auth/AdminPinAuth';
import { AdminSettings } from '@/components/admin/AdminSettings';
import { LoginAttemptsPage } from '@/components/admin/LoginAttemptsPage';
import { StoreStationEntry } from '@/components/store-station/StoreStationEntry';

const Index = () => {
  const { profile, isAdmin } = useAuth();
  const { currentMode } = useUserMode();
  const navigate = useNavigate();
  
  const [searchParams, setSearchParams] = useSearchParams();
  const { 
    isAdminAuthenticated, 
    isCheckingAuth, 
    authenticateAdmin, 
    signOutAdmin 
  } = useAdminAuth();
  
  const [showAdminPin, setShowAdminPin] = useState(false);
  const [showAdminSettings, setShowAdminSettings] = useState(false);
  const [showStoreStationEntry, setShowStoreStationEntry] = useState(false);
  
  const {
    currentView,
    setCurrentView,
    selectedOrder,
    setSelectedOrder,
    networkStatus,
    userName,
    orders,
    ordersLoading,
    ordersError,
    handleOrderCheckIn,
    handleViewProducts,
    handleEditOrder,
    handleDeleteOrder,
    checkNetworkStatus
  } = useIndexState();

  // Role-based dashboard redirection (server-verified roles only)
  // Redirect logic removed to prevent redirect loops
  // Users can navigate to dashboards manually or via navigation components

  // Handle URL parameters for views
  useEffect(() => {
    const view = searchParams.get('view');
    if (view && view !== 'checkin') {
      setCurrentView(view as any);
      // Clean up URL after setting view
      setSearchParams({});
    } else if (view === 'checkin') {
      // Redirect to dedicated checkin page
      navigate('/checkin', { replace: true });
    }
  }, [searchParams, setCurrentView, setSearchParams, navigate]);


  const handlePriceCheck = () => {
    setCurrentView("prices");
  };

  const handleEmployeeClockIn = () => {
    setCurrentView("employee-clockin");
  };

  const handleEmployeeManagement = () => {
    setCurrentView("employee-management");
  };

  const handleViewBackend = () => {
    setCurrentView("backend");
  };

  const handleCreditReports = () => {
    navigate('/credit-reports');
  };

  const handleCostMonitor = () => {
    navigate('/cost-monitor');
  };

  const handleMasterProducts = () => {
    navigate('/master-products');
  };

  const handleNavigateToMissingProducts = () => {
    navigate('/non-scanning-products');
  };

  const handleBack = () => {
    setCurrentView("dashboard");
    setSelectedOrder(null);
    setShowAdminSettings(false);
  };

  const handleSave = () => {
    setCurrentView("dashboard");
    setSelectedOrder(null);
  };

  const handleSubmit = (orderData: any) => {
    handleOrderCheckIn(orderData);
    setCurrentView("dashboard");
  };

  const handleViewTimeCalendar = () => {
    setCurrentView("employee-time-calendar");
  };

  const handleRequestAdminAccess = () => {
    setShowAdminPin(true);
  };

  const handleAdminPinSuccess = () => {
    authenticateAdmin();
    setShowAdminPin(false);
  };

  const handleAdminSettings = () => {
    setShowAdminSettings(true);
  };

  const handleSignOutAdmin = () => {
    signOutAdmin();
    setShowAdminSettings(false);
  };

  const handleArchivedOrders = () => {
    navigate('/archived-orders');
  };

  const handleLoginAttempts = () => {
    setCurrentView('login-attempts');
  };

  const handleStoreStationEntry = () => {
    setShowStoreStationEntry(true);
  };

  const handleStoreStationEntryBack = () => {
    setShowStoreStationEntry(false);
  };

  // Show Store Station entry if requested (before authentication)
  if (showStoreStationEntry) {
    return <StoreStationEntry onBack={handleStoreStationEntryBack} />;
  }

  // Show loading while checking admin auth
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Show admin PIN prompt
  if (showAdminPin) {
    return (
      <AuthGuard onStoreStationAccess={handleStoreStationEntry}>
        <AdminPinAuth 
          onAuthSuccess={handleAdminPinSuccess}
          onCancel={() => setShowAdminPin(false)}
        />
      </AuthGuard>
    );
  }

  // Show admin settings
  if (showAdminSettings) {
    return (
      <AuthGuard onStoreStationAccess={handleStoreStationEntry}>
        <AdminSettings onBack={handleBack} />
      </AuthGuard>
    );
  }

  // Show login attempts page
  if (currentView === 'login-attempts') {
    return (
      <AuthGuard onStoreStationAccess={handleStoreStationEntry}>
        <LoginAttemptsPage 
          userName={profile?.full_name || profile?.email || null}
          networkStatus={networkStatus}
          onNetworkCheck={checkNetworkStatus}
          onNewOrder={() => navigate('/checkin')}
          onPriceCheck={handlePriceCheck}
          onEmployeeClockIn={handleEmployeeClockIn}
          onEmployeeManagement={handleEmployeeManagement}
          onViewBackend={handleViewBackend}
          onCreditReports={handleCreditReports}
          onCostMonitor={handleCostMonitor}
          onMasterProducts={handleMasterProducts}
          onAdminSettings={handleAdminSettings}
          onArchivedOrders={handleArchivedOrders}
          onSignOutAdmin={handleSignOutAdmin}
          onLoginAttempts={handleLoginAttempts}
          onBack={handleBack} 
        />
      </AuthGuard>
    );
  }

  // Show other views if not dashboard
  if (currentView !== "dashboard") {
    return (
      <AuthGuard onStoreStationAccess={handleStoreStationEntry}>
        <ViewRouter
          currentView={currentView}
          selectedOrder={selectedOrder}
          userName={profile?.full_name || profile?.email || null}
          networkStatus={networkStatus}
          onBack={handleBack}
          onSave={handleSave}
          onDelete={handleDeleteOrder}
          onSubmit={handleSubmit}
          onViewTimeCalendar={handleViewTimeCalendar}
          onNetworkCheck={checkNetworkStatus}
          onNewOrder={() => navigate('/checkin')}
          onPriceCheck={handlePriceCheck}
          onEmployeeClockIn={handleEmployeeClockIn}
          onEmployeeManagement={handleEmployeeManagement}
          onViewBackend={handleViewBackend}
          onCreditReports={handleCreditReports}
          onCostMonitor={handleCostMonitor}
          setCurrentView={setCurrentView}
          onLoginAttempts={handleLoginAttempts}
        />
      </AuthGuard>
    );
  }

  // Render main dashboard with navigation options
  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto p-6">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">Welcome to Foodland Order Eye</h1>
          <p className="text-muted-foreground">
            {profile?.full_name || profile?.email}
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">Quick Actions</h2>
            
            <button
              onClick={() => navigate(isAdmin ? '/admin-dashboard' : '/staff-dashboard')}
              className="w-full p-4 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
            >
              Go to {isAdmin ? 'Admin' : 'Staff'} Dashboard
            </button>
            
            <button
              onClick={handlePriceCheck}
              className="w-full p-4 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/90 transition-colors"
            >
              Price Check
            </button>
            
            <button
              onClick={() => navigate('/checkin')}
              className="w-full p-4 bg-accent text-accent-foreground rounded-lg hover:bg-accent/90 transition-colors"
            >
              New Order Check-in
            </button>
          </div>
          
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">System Status</h2>
            <div className="p-4 border rounded-lg">
              <p className="text-sm text-muted-foreground">
                Network: <span className="text-foreground">{networkStatus}</span>
              </p>
              <p className="text-sm text-muted-foreground">
                Role: <span className="text-foreground">{profile?.role || 'staff'}</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Index;
